import { useMemo } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { useGetAllTagValues } from "src/api/client-api/tag/useGetAllTagValues";
import { PageBackground } from "src/components/EntityForm/PageBackground";
import { SectionCard } from "src/components/EntityForm/SectionCard";
import { resolveTagColors } from "src/components/TagManagement/tagColors";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    CategoryTagList,
    DisplayTagList,
    InfoField,
    InformationDivider,
    InfoSection,
    productInformationNoValue as noValue,
    type DisplayTag,
} from "src/pages/Product/v2/ProductInformationsV2/ProductInformationDisplay";
import { URLHandler } from "src/services/URLHandler";
import type { TagType, TagValue } from "src/types/tag";
import { useIngredientContext } from "../useIngredientContext";

/** Ingredient information overview aligned with Figma `12819:70397`. */
export const IngredientInformationsV2 = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedCompany } = useFoodpilotContext();
    const { ingredient, ingredientReference, showIngredientSuppliers } = useIngredientContext();

    const { tagTypes = [] } = useGetAllTagTypes({ context: "ingredient", itemsPerPage: 100, active: true });
    const { data: tagValuesResponse } = useGetAllTagValues({ itemsPerPage: 1000 });
    const allTagValues: TagValue[] = useMemo(() => tagValuesResponse?.tagValues ?? [], [tagValuesResponse?.tagValues]);
    const slugToValue = useMemo(
        () =>
            allTagValues.reduce<Record<string, TagValue>>((values, value) => {
                values[value.slug] = value;
                return values;
            }, {}),
        [allTagValues],
    );

    if (ingredient === undefined) return null;

    const associatedEntityNames =
        ingredient.shares
            ?.map(({ organization }) => organization.name)
            .filter((name) => name !== (ingredient.ownedBy?.name ?? selectedCompany?.name)) ?? [];
    const supplierTags: DisplayTag[] =
        ingredient.suppliers?.map(({ supplier }) => ({
            key: supplier["@id"],
            label: supplier.name,
        })) ?? [];

    const resolveTagValuesForType = (tagType: TagType): TagValue[] =>
        (ingredient.tags?.[tagType.slug] ?? []).reduce<TagValue[]>((values, slug) => {
            const value = slugToValue[slug];
            if (value) values.push(value);
            return values;
        }, []);

    const customTagItems = (tagType: TagType): DisplayTag[] =>
        resolveTagValuesForType(tagType).map((value) => {
            return {
                key: value.slug,
                label: value.name,
                colors: resolveTagColors(theme, tagType, value),
            };
        });

    const handleEdit = () => {
        if (selectedCompany === null) return;

        const currentUrl = location.pathname + location.search;
        const editUrl = URLHandler.ingredient.edit(selectedCompany.id, ingredient.id, 0);
        navigate(`${editUrl}?returnUrl=${encodeURIComponent(currentUrl)}`);
    };

    return (
        <PageBackground>
            <Stack padding={theme.spacing(3, 0, 4)} gap={theme.spacing(3)}>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_INFORMATION_HEADER} />
                <Stack
                    data-testid="ingredient-information-header"
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "stretch" : "center"}
                    gap={theme.spacing(2)}
                >
                    <Stack flex={1} minWidth={0}>
                        <Typography variant="h3">{ingredient.name}</Typography>
                        <Stack direction="row" gap={theme.spacing(1)}>
                            <Typography variant="small-medium">
                                {t("product.v2.informations.owning_entity", "Owning entity:")}
                            </Typography>
                            <Typography variant="small-regular" sx={{ color: theme.custom.grey[2500] }}>
                                {ingredient.ownedBy?.name ?? selectedCompany?.name ?? noValue}
                            </Typography>
                        </Stack>
                    </Stack>
                    {ingredient.canModify !== false && (
                        <Button
                            variant="primary"
                            onClick={handleEdit}
                            startIcon={<FoodsIcon icon="edit" size={2} />}
                            sx={{
                                borderRadius: theme.spacing(1),
                                boxShadow: theme.custom.shadows.hard,
                                gap: theme.spacing(1),
                                height: theme.spacing(5),
                                paddingLeft: theme.spacing(2),
                                paddingRight: theme.spacing(2.5),
                                width: isMobile ? "100%" : undefined,
                                "& .MuiButton-startIcon": { margin: theme.spacing(0) },
                            }}
                        >
                            {t("product.v2.informations.quick_edit", "Edit")}
                        </Button>
                    )}
                </Stack>

                <Box
                    data-testid="ingredient-information-content"
                    sx={{
                        alignItems: "start",
                        display: "grid",
                        gap: theme.spacing(3),
                        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                    }}
                >
                    <SectionCard data-testid="ingredient-information-left-card">
                        <Stack padding={theme.spacing(3)}>
                            <InfoSection
                                sectionKey="identity"
                                title={t("product.v2.informations.identity", "Identity")}
                            >
                                <InfoField
                                    label={`${t("product.v2.identity.unique_identifier", "Unique identifier")}:`}
                                    value={ingredient.clientId}
                                />
                                <InfoField
                                    label={`${t(
                                        "product.v2.identity.associated_reference_product",
                                        "Associated reference product",
                                    )}:`}
                                    value={ingredientReference?.name}
                                    tone="secondary"
                                />
                                <InfoField
                                    label={`${t("product.v2.identity.organic_product", "Organic product")}:`}
                                    value={
                                        <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)}>
                                            {ingredient.organic === true && <FoodsIcon icon="check" size={2} />}
                                            <Typography variant="body-regular" sx={{ lineHeight: theme.spacing(2.75) }}>
                                                {ingredient.organic === true ?
                                                    t("common.yes", "Yes")
                                                :   t("common.no", "No")}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </InfoSection>
                        </Stack>
                    </SectionCard>

                    <SectionCard data-testid="ingredient-information-right-card">
                        <Stack padding={theme.spacing(3)} gap={theme.spacing(3)}>
                            <InfoSection
                                sectionKey="organization"
                                title={t("product.v2.organization.section_title", "Organization")}
                            >
                                <InfoField
                                    label={`${t("settings.sector", "Sector")}:`}
                                    value={ingredient.ingredientLine?.name}
                                />
                                <InfoField
                                    label={t("entity.organization.associated_entities.label", "Associated entities:")}
                                    value={<CategoryTagList labels={associatedEntityNames} />}
                                />
                                {tagTypes.map((tagType) => (
                                    <InfoField
                                        key={tagType.id}
                                        label={`${tagType.name}:`}
                                        value={<DisplayTagList items={customTagItems(tagType)} />}
                                    />
                                ))}
                            </InfoSection>
                            {showIngredientSuppliers && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="suppliers"
                                        title={`${t("ingredient.v2.informations.suppliers", "Suppliers")} (${supplierTags.length})`}
                                    >
                                        <DisplayTagList items={supplierTags} spacing="lifecycle" />
                                    </InfoSection>
                                </>
                            )}
                        </Stack>
                    </SectionCard>
                </Box>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_INFORMATION_FOOTER} />
            </Stack>
        </PageBackground>
    );
};
